import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { commercialConstants } from 'app/common/commercial-constants';
import { PersonalConstants } from 'app/common/personalConstants';
import { CancelClaimModalComponent } from 'app/shared/components/cancel-modal/cancel-modal.component';
import { BoltFormHelper } from 'app/shared/services/bolt-form-helper';
import { LoggerService } from 'app/shared/services/logger.service';
import { environment } from 'environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { Observable } from 'rxjs';

@Component({
    selector: 'is-test-page',
    templateUrl: './test-page.component.html',
    styleUrls: ['./test-page.component.scss'],
    standalone: false
})
export class TestPageComponent implements OnInit {
    @ViewChild(CancelClaimModalComponent)
    cancelClaimModal!: CancelClaimModalComponent;
    iamBypassForm!: UntypedFormGroup;
    showResults = false;
    showError = false;
    showWait = false;
    showServiceError = false;
    serviceBaseErrMsg =
        'Something went wrong with the token call. Your guess is as good as mine as to what it was...';
    serviceErrMsg = '';
    links: any;
    historyList: string[] = [];
    hasHistory = false;
    deployItems: any = {};
    isTableVisible = false;
    iamBypassDTO = {
        usernameText: '',
        environment: '',
        ccEnv: ''
    };
    environmentList: any;
    @ViewChild('environmentModal', { static: true })
    environmentModal!: ElementRef;
    usertype = 'unregistered';
    originalOrder = (): number => 0;
    private readonly pakAgreementPrefixes: string[] = [
        ...commercialConstants.packageInfo.reduce(
            (allPrefixes: string[], packageType: { prefixes?: string[] }) => {
                if (packageType?.prefixes?.length) {
                    packageType.prefixes.forEach((prefix) => {
                        allPrefixes.push((prefix || '').toUpperCase());
                    });
                }
                return allPrefixes;
            },
            []
        ),
        ...(commercialConstants.farmingPrefixes || []).map((prefix) =>
            (prefix || '').toUpperCase()
        )
    ];

    constructor(
        private readonly httpClient: HttpClient,
        private fh: BoltFormHelper,
        private cookieService: CookieService,
        private readonly logger: LoggerService
    ) {}

    userList: any[] = [];

    ngOnInit(): void {
        this.preloadHistory();
        this.buildForm();
        this.fetchBranchConfigDeployItems();
        this.iamBypassForm.valueChanges.subscribe((changes) => {
            this.iamBypassDTO.usernameText =
                this.iamBypassForm.get('usernameText')?.value;
            this.iamBypassDTO.environment =
                this.iamBypassForm.get('environment')?.value;
            this.iamBypassDTO.ccEnv = this.iamBypassForm.get('ccEnv')?.value;
        });
        this.loadUserList();
    }

    buildForm(): void {
        this.iamBypassForm = this.fh.build({
            usernameText: [''],
            environment: [''],
            ccEnv: ['']
        });
    }

    openModal(): any {
        this.environmentModal.nativeElement.openModal();
    }

    preloadHistory(): void {
        if (this.cookieService.check(environment.claimsCookies.historyCookieName)) {
            const historyCookie = this.cookieService.get(
                environment.claimsCookies.historyCookieName
            );
            this.hasHistory = true;
            const historyCookieObj = JSON.parse(historyCookie);
            this.historyList = historyCookieObj;
        } else {
            this.logger.info('No history found');
        }
    }

    saveHistory(username: string): void {
        if (this.hasHistory) {
            if (!this.historyList.includes(username)) {
                this.historyList.push(username);
                this.cookieService.set(
                    environment.claimsCookies.historyCookieName,
                    JSON.stringify(this.historyList),
                    environment.claimsCookies.cookieOptions
                );
            }
        } else {
            this.historyList.push(username);
            this.cookieService.set(
                environment.claimsCookies.historyCookieName,
                JSON.stringify(this.historyList),
                environment.claimsCookies.cookieOptions
            );
            this.hasHistory = true;
        }
    }

    deleteItem(item: any): boolean {
        const index = this.historyList.indexOf(item);
        if (index > -1) {
            this.historyList.splice(index, 1);
            this.cookieService.set(
                environment.claimsCookies.historyCookieName,
                JSON.stringify(this.historyList),
                environment.claimsCookies.cookieOptions
            );
        }
        return false;
    }

    callEUA(username: string): Observable<any> {
        const formData = {
            grant_type: 'password',
            scope: 'openid',
            realm: 'member',
            auth_method: 'ping-sts',
            username: username,
            password: environment.iamBypass.acctInfo
        };
        const EUA2_TOKEN_FULL = environment.MONTY_AUTH_URL;
        const FULL_PARAMS = {
            headers: this.getEUAServiceHeaders()
        };
        const formPayload = Object.keys(formData)
            .map((key) => `${key}=${formData[<keyof typeof formData>key]}`)
            .join('&');

        return this.httpClient.post(EUA2_TOKEN_FULL, formPayload, FULL_PARAMS);
    }

    getEUAServiceHeaders(): HttpHeaders {
        const BASIC_AUTH = environment.iamBypass.basicAuth;

        let headers: HttpHeaders = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded'
        });
        headers = headers.set(
            PersonalConstants.headerProps.AUTHORIZATION,
            `Basic ${BASIC_AUTH}`
        );

        return headers;
    }

    parseJWTBody(response: any): any {
        const jwtPayload = response.split('.')[1];
        const decodedJWT = atob(jwtPayload).toString();
        return JSON.parse(decodedJWT);
    }

    clear(): any {
        this.cookieService.delete(environment.claimsCookies.historyCookieName);
        this.cookieService.delete(environment.claimsCookies.claimsEnvNumber);
        this.cookieService.delete(environment.claimsCookies.claimsEnv);
        this.historyList = [];
        return false;
    }

    cancel(): void {
        this.cancelClaimModal.cancelClaimModalOpen();
    }

    setEnv(env: string): any {
        this.cookieService.set(
            environment.claimsCookies.claimsEnvNumber,
            this.iamBypassDTO.ccEnv,
            environment.claimsCookies.cookieOptions
        );
        this.cookieService.set(
            environment.claimsCookies.claimsEnv,
            this.getEnvironment(env),
            environment.claimsCookies.cookieOptions
        );
    }

    continue(): void {
        this.showServiceError = false;
        this.showWait = true;
        this.showResults = false;
        const username = this.iamBypassDTO.usernameText;
        const env = this.iamBypassDTO.environment;
        const ccEnv = this.iamBypassDTO.ccEnv;
        let ccEnvAndServerNumber: any;
        if (username && env) {
            this.showError = false;
            const AUTHKEY = environment.iamBypass.authKey;

            ccEnvAndServerNumber = !ccEnv ? 'unset' : `test${ccEnv}`;
            if (ccEnv) this.setEnv(env);
            try {
                this.callEUA(username).subscribe({
                    next: (response) => {
                        const {
                            access_token: accessToken,
                            expires_in: expiresIn,
                            id_token: idToken
                        } = response;

                        const parsedIdToken = this.parseJWTBody(
                            response.id_token
                        );
                        this.links = parsedIdToken.agreements
                            .filter(
                                ({ product_type: productType }: { product_type: string }) =>
                                    productType.toLowerCase() !== 'billing'
                            )
                            .map(
                                ({
                                    agreement_number: agreementNumber,
                                    product_type: productType
                                }: { agreement_number: string; product_type: string }) => {
                                    let host = '';
                                    let address = '';
                                    const agreementObj = {
                                        env: this.getEnvironment(env),
                                        productType: productType,
                                        agreementNumber: agreementNumber,
                                        url: ''
                                    };

                                    if (
                                        productType === 'Auto' ||
                                        productType === 'Personal Auto'
                                    ) {
                                        host =
                                            environment.iamBypass.newApp.urls[
                                                env
                                            ];
                                        const isBusinessAutoAgreement =
                                            productType === 'Auto' &&
                                            this.shouldRouteToPakClaimsList(
                                                agreementNumber
                                            );
                                        const indexPath =
                                            isBusinessAutoAgreement ?
                                                environment.iamBypass.newApp
                                                    .commercialIndexPath[env] :
                                                environment.iamBypass.newApp
                                                    .indexPath[env];

                                        address =
                                            `https://${host}${indexPath}` +
                                            `?access_token=${accessToken}` +
                                            `&expires_in=${expiresIn}` +
                                            `&id_token=${idToken}` +
                                            `&pkey=${agreementNumber}` +
                                            `&encodedKey=${AUTHKEY}` +
                                            `&IAM_BYPASS=true`;
                                    } else {
                                        host =
                                            environment.iamBypass.newApp.urls[
                                                env
                                            ];
                                        const shouldRouteToPak =
                                            this.shouldRouteToPakClaimsList(
                                                agreementNumber
                                            );
                                        const indexPath =
                                            shouldRouteToPak ?
                                                environment.iamBypass.newApp
                                                    .commercialIndexPath[env] :
                                                environment.iamBypass.newApp
                                                    .propertyIndexPath[env];

                                        address =
                                            `https://${host}${indexPath}` +
                                            `?access_token=${accessToken}` +
                                            `&expires_in=${expiresIn}` +
                                            `&id_token=${idToken}` +
                                            `&pkey=${agreementNumber}` +
                                            `&encodedKey=${AUTHKEY}` +
                                            `&IAM_BYPASS=true`;
                                    }
                                    agreementObj.url = address;
                                    return agreementObj;
                                }
                            );
                        this.usertype = 'registered';
                        this.saveHistory(username);
                        this.showWait = false;
                        this.showResults = true;
                    },
                    error: (err) => {
                        this.showServiceError = true;
                        this.serviceErrMsg = err?.developerMessage;
                        this.showWait = false;
                    }
                });
            } catch (err) {
                this.showServiceError = true;
                this.serviceErrMsg = this.serviceBaseErrMsg;
                this.showWait = false;
            }
        } else {
            this.showWait = false;
            this.showError = true;
        }
    }

    prefill(username: any): any {
        this.iamBypassForm.get('usernameText')?.setValue(username);
        this.isTableVisible = false;
        return false;
    }

    getEnvironment(env: string): any {
        if (env === 'LOCALDEV') {
            return 'dev';
        } else if (env === 'LOCALTEST') {
            return 'test';
        }
        return env;
    }

    findLatestCommit(
        branch: any,
        branchData: Array<{ [key: string]: any }>
    ): any {
        const gitCommit =
            'https://github.com/Nationwide/dgs-internet-servicing-claims/commit/';
        const commit = branchData.find((obj) => {
            if (obj.name === branch) {
                return obj;
            } else {
                return false;
            }
        });

        return commit !== undefined ? gitCommit + commit.commit.sha : undefined;
    }

    autoUsers: any[] = [];
    propertyUsers: any[] = [];
    comboUsers: any[] = [];

    private assignUserCategories(): void {
        this.autoUsers = this.userList.filter((user) => user.policyType === 'Auto');
        this.propertyUsers = this.userList
            .filter((user) =>
                ['Property', 'Condo', 'Tenant', 'Dwelling Fire & Auto'].includes(user.policyType)
            )
            .sort((a, b) => a.userId.localeCompare(b.userId));
        this.comboUsers = this.userList.filter((user) => user.policyType === 'Combo');
    }

    loadUserList(): void {
        this.httpClient.get<any[]>('assets/user-list.json').subscribe({
            next: (data) => {
                this.userList = data;
                this.assignUserCategories();
            },
            error: (err) => {
                this.logger.error('Failed to load user list', err);
            }
        });
    }

    toggleTable(): void {
        this.isTableVisible = !this.isTableVisible;
    }

    private shouldRouteToPakClaimsList(agreementNumber: string): boolean {
        const normalizedAgreementNumber = (agreementNumber || '').toUpperCase();

        return this.pakAgreementPrefixes.some((prefix) =>
            normalizedAgreementNumber.startsWith(prefix)
        );
    }


    private async fetchBranchConfigDeployItems(): Promise<void> {
        const owner = 'Nationwide';
        const repo = 'internet-servicing-claims-pipelines';
        const filePath = '.github/workflows/branch-config.yaml'; // update if in a subdirectory
        const branch = 'main'; // update to 'main' if needed
        const url = `https://api.github.com/repos/${owner}/${repo}/contents/${filePath}?ref=${branch}`;

        try {
            const response = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${environment.token}`,
                    Accept: 'application/vnd.github.v3+json'
                }
            });

            if (!response.ok) {
                const errorBody = await response.text();
                this.logger.error(
                    `GitHub API error ${response.status} for URL: ${url} — ${errorBody}`
                );
                return;
            }

            const data = await response.json();
            const decoded = atob(data.content.replace(/\n/g, ''));
            this.deployItems = this.parseDeployKeys(decoded);
            this.environmentList = this.transformJobsToEnvironmentList(this.deployItems);
            this.logger.info('Deploy items fetched successfully', this.deployItems);
            this.logger.info('Environment list populated', this.environmentList);
        } catch (error) {
            this.logger.error(`fetchBranchConfigDeployItems failed: ${error}`);
        }
    }

    private parseDeployKeys(yamlContent: string): any {
        const lines = yamlContent.split('\n');
        let insideJobs = false;
        let jobsIndent = 0;
        const jobs: any = {};
        const stack: Array<{ obj: any; key: string; indent: number }> = [];

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const trimmed = line.trim();

            // Skip empty lines and comments
            if (!trimmed || trimmed.startsWith('#')) {
                continue;
            }

            // Detect jobs section
            if (/^jobs:/.test(line)) {
                insideJobs = true;
                jobsIndent = line.search(/\S/);
                continue;
            }

            if (!insideJobs) {
                continue;
            }

            const indent = line.search(/\S/);

            // If we're back to the same or less indentation as jobs:, we're done
            if (indent <= jobsIndent && trimmed !== '') {
                break;
            }

            // Parse key-value pairs
            const colonIndex = line.indexOf(':');
            if (colonIndex === -1) {
                continue;
            }

            const key = line.substring(indent, colonIndex).trim();
            const valueStr = line.substring(colonIndex + 1).trim();

            // Determine the correct parent object based on indentation
            while (stack.length > 0 && indent <= stack[stack.length - 1].indent) {
                stack.pop();
            }

            const parentObj = stack.length > 0 ? stack[stack.length - 1].obj : jobs;

            // Parse value
            let value: any;
            if (!valueStr || valueStr === '') {
                // This is an object/array parent
                value = {};
                parentObj[key] = value;
                stack.push({ obj: value, key, indent });
            } else if (valueStr.startsWith('[')) {
                // Inline array
                try {
                    value = JSON.parse(valueStr);
                } catch {
                    value = valueStr;
                }
                parentObj[key] = value;
            } else if (valueStr.startsWith('\'') || valueStr.startsWith('"')) {
                // Quoted string
                value = valueStr.replace(/^['"]|['"]$/g, '');
                parentObj[key] = value;
            } else if (valueStr === 'true' || valueStr === 'false') {
                // Boolean
                parentObj[key] = valueStr === 'true';
            } else if (!isNaN(Number(valueStr))) {
                // Number
                parentObj[key] = Number(valueStr);
            } else {
                // String
                parentObj[key] = valueStr;
            }
        }

        return jobs;
    }

    private transformJobsToEnvironmentList(jobs: any): Array<{ name: string; branch: string }> {
        const envList: Array<{ name: string; branch: string }> = [];

        if (!jobs || typeof jobs !== 'object') {
            return envList;
        }

        // Iterate over all job keys
        Object.keys(jobs).forEach((jobName) => {
            // Only process jobs that start with 'deploy-'
            if (jobName.startsWith('deploy-')) {
                // Parse job name pattern: deploy-{environment}-{bucket}-{branch}
                // Example: deploy-test-G-tracker-mvp2 or deploy-prod-A-development
                const parts = jobName.split('-');

                if (parts.length >= 4) {
                    const envType = parts[1]; // 'test' or 'prod'
                    const bucket = parts[2]; // 'G', 'A', etc.
                    const branch = parts.slice(3).join('-'); // everything after bucket, rejoined with dashes

                    envList.push({
                        name: `${envType}-${bucket}`,
                        branch: branch
                    });
                }
            }
        });

        // Sort by name for better readability
        envList.sort((a, b) => a.name.localeCompare(b.name));

        return envList;
    }
}
